import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import LiveScoreMatchLeagues from "../LiveScoreMatchLeagues";
import LiveScoreMatch from "../LiveScoreMatch";
import LoadMoreButton from "../LoadMoreButton";
import LiveScoreHeader from "../LiveScoreHeader";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvLiveScore = ({ type = "all" }) => {

  const { colorPrimary, colorSecondary, colorBg, colorText, colorForeground, hasDataConfig } = useCompConfig();
  const themeStyle = hasDataConfig
    ? {
      "--theme-primary": colorPrimary,
      "--theme-highlight": colorSecondary,
      "--theme-bg": colorBg,
      "--theme-text": colorText,
      "--theme-foreground": colorForeground,
    }
    : undefined;

  const [displayLimit, setDisplayLimit] = useState(20);

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const baseQuery = {
    include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
    filterOdds: -1,
    cache: false,
    page: 1,
  };

  const queryLive = useMemo(
    () => ({
      ...baseQuery,
      filterInclude: `live`,
      status: "live",
      limit: 100,
    }),
    [],
  );

  const queryObject = useMemo(
    () => ({
      ...baseQuery,
      filterInclude: `date:${todayString}`,
      status: type === "ketqua" ? "end" : "",
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [todayString, type],
  );

  const {
    leaguesWithMatches, // Represents today's matches
    loading: todayLoading,
    error: todayError,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const {
    leaguesWithMatches: liveLeagues,
    loading: liveLoading,
  } = useBangKeoMatches(queryLive, false);

  const loading = todayLoading || liveLoading;
  const error = todayError;

  const baseLeaguesWithMatches = useMemo(() => {
    let allData = [];

    // 1. Add live data (Always First)
    (liveLeagues || []).forEach(item => {
      allData.push({ ...item, matches: [...(item.matches || [])] });
    });

    // 2. Add today data, merging matches if league exists
    (leaguesWithMatches || []).forEach(item => {
      const exist = allData.find(d => String(d.league?.id) === String(item.league?.id));
      if (exist) {
        const newMatches = item.matches || [];
        const existingMatches = exist.matches || [];
        const diff = newMatches.filter(m => !existingMatches.find(em => em.id === m.id));
        exist.matches = [...existingMatches, ...diff];
      } else {
        allData.push({ ...item, matches: [...(item.matches || [])] });
      }
    });

    return allData.map((item) => {
      const normalizedMatches = Array.isArray(item?.matches)
        ? item.matches
        : Object.values(item?.matches || {});
      return {
        ...item,
        matches: normalizedMatches.filter(Boolean),
      };
    });
  }, [liveLeagues, leaguesWithMatches]);

  const allFilteredMatches = useMemo(() => {
    return baseLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [baseLeaguesWithMatches]);

  const visibleMatches = useMemo(() => {
    return allFilteredMatches.slice(0, displayLimit);
  }, [allFilteredMatches, displayLimit]);

  const groupedVisibleMatches = useMemo(() => {
    const groups = [];
    visibleMatches.forEach((match) => {
      const leagueId = String(match.league?.id);
      let group = groups.find((g) => String(g.league?.id) === leagueId);
      if (!group) {
        group = { league: match.league, matches: [] };
        groups.push(group);
      }
      group.matches.push(match);
    });
    return groups;
  }, [visibleMatches]);

  const lastTableMatchId = useMemo(() => {
    if (!groupedVisibleMatches.length) return undefined;
    const lastMatches = groupedVisibleMatches[groupedVisibleMatches.length - 1]?.matches;
    if (!lastMatches?.length) return undefined;
    return lastMatches[lastMatches.length - 1]?.id;
  }, [groupedVisibleMatches]);

  useEffect(() => {
    // Auto-fetch if filtered results are below display limit
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      baseLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [baseLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  const [activeOddsPopup, setActiveOddsPopup] = useState({ 
    matchId: null, 
    cellIdx: null 
  });

  const handleShowOddsPopup = (matchId, cellIdx) => {
    setActiveOddsPopup({ matchId, cellIdx });
  };

  const handleHideOddsPopup = () => {
    setActiveOddsPopup({ matchId: null, cellIdx: null });
  };

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-2 py-3 bg-[var(--kbv-bg-page)]"
      style={themeStyle}
    >

      {loading && <Loading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="rounded-lg">
            {groupedVisibleMatches.length === 0 ? (
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            ) : (
              <>
                <div
                  className={`kbv-livescore-wrap w-full ${
                    activeOddsPopup?.matchId != null
                      ? "overflow-x-visible overflow-y-visible"
                      : "overflow-x-auto md:overflow-x-visible"
                  }`}
                >
                  <table
                    className="table-time table-game-rate mb-0 w-full table border-collapse border-spacing-0 bg-transparent text-center max-md:text-[color:var(--theme-text-muted)] md:text-[color:var(--theme-text)]"
                  >
                    <LiveScoreHeader />
                    {groupedVisibleMatches.map(({ league, matches }, leagueIndex) => (
                      <LiveScoreMatchLeagues
                        key={league?.id || `league-${leagueIndex}`}
                        league={league || {}}
                        matches={matches}
                        lastTableMatchId={lastTableMatchId}
                        onShowPopup={handleShowOddsPopup}
                        onHidePopup={handleHideOddsPopup}
                        activePopup={activeOddsPopup}
                        render={(match, idx, handlers) =>
                          match ? (
                            <LiveScoreMatch
                              key={match.id || `${league?.id || leagueIndex}-${match?.matchTime || Math.random()}`}
                              match={match}
                              isLastInTable={
                                lastTableMatchId != null && String(match.id) === String(lastTableMatchId)
                              }
                              onShowPopup={handlers?.onShowPopup}
                              onHidePopup={handlers?.onHidePopup}
                              activePopup={handlers?.activePopup}
                            />
                          ) : null
                        }
                      />
                    ))}
                  </table>
                </div>
              </>
            )}
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={() => setDisplayLimit((prev) => prev + 20)}
            loadingComponent={<Loading />}
            hasMore={allFilteredMatches.length > displayLimit || hasMore}
          />
        </>
      )}
    </div>
  );
};

export default KbvLiveScore;
